import { useHierarchyArea } from '@plarium/hierarchy-react';
import React, { useState, useMemo, useCallback, useEffect } from 'react';

import { useField, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import { useRegisteredRef } from '@gogame/components';

import { useFormFieldGroupState } from '../FormFieldGroup';
import { validateSchema } from '../utils';
import { FieldWrapper } from './components';

const FormHierarchyField = ({
  name: _name,
  active: _active = true,
  disabled: _disabled = false,
  children,
  basis,
  grow,
  order,
  shrink,
  vertical,
  inline,
  padding,
  width,
  depends,
  source,
  onDataLoad,
  params,
  normalizeValue,
  fast,
  onBlur,
  onFocus,
  onChange,
  isEmpty = (v) => v === undefined,
  validate,
  validationSchema,
  lazy = false,
  keepStateOnUnmount,
  hiddenField,
  disableError = false,
  _cleanOnUnmount = false,
  ...props
}) => {
  const { submittingMetaRef, ...formik } = useFormikContext();
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    return () => {
      if (_cleanOnUnmount) {
        formik.setFieldValue(name, undefined);
      }
    };
  }, [_cleanOnUnmount]);

  const { name, active, disabled } = useFormFieldGroupState({
    name: _name,
    active: _active,
    disabled: _disabled,
  });

  const validateFn = useMemo(() => {
    let fn;

    if (typeof validate === 'function') {
      fn = (value) => validate(value, submittingMetaRef.current, formik.values);
    } else if (validationSchema) {
      fn = (value) =>
        validateSchema(value, validationSchema, { context: formik.values });
    }
    return fn;
  }, [validate, validationSchema]);

  const [field, meta] = useField({
    name,
    debounceTimeout: props.debounceTimeout,
    onChange,
    validate: validateFn,
  });

  const nodeRef = useRegisteredRef(name);

  const handleDataLoad = useCallback(
    (area) => {
      onDataLoad({
        data: area.data,
        error: area.error,
        name: name,
        form: formik,
      });
    },
    [onDataLoad]
  );

  const empty = isEmpty(field.value);

  const hierarchyArea = useHierarchyArea({
    depends,
    source,
    name,
    active: active && (!lazy || meta.touched || !empty || focused), ///&& (!lazy || !isEmpty(field.value)),
    onDataLoad: onDataLoad && handleDataLoad,
    params,
    normalizeValue,
    keepStateOnUnmount,
  });

  const fieldOnBlurRef = React.useRef(null);

  fieldOnBlurRef.current = field.onBlur;

  const handleFieldBlur = useCallback(
    (e) => {
      setFocused(false);
      onBlur && onBlur(e);
      fieldOnBlurRef.current(e);
    },
    [onBlur]
  );

  const handleFieldFocus = useCallback(
    (e) => {
      setFocused(true);
      onFocus && onFocus(e);
    },
    [onFocus]
  );

  const bag = {
    field: {
      ...field,
      onChange: (e, value) => {
        formik.setFieldValue(name, value);
        if (onChange) {
          onChange(e, value);
        }
      },
      onBlur: handleFieldBlur,
      onFocus: handleFieldFocus,
    },
    error: disableError ? null : meta.error || hierarchyArea.error,
    meta,
    initialValue: meta.initialValue,
    area: hierarchyArea,
    ...props,
    form: formik,
    active: hierarchyArea.active,
    disabled: disabled || !active,
  };

  const fastField = useMemo(() => {
    return (
      <FieldWrapper
        padding={padding}
        basis={basis}
        grow={grow}
        order={order}
        shrink={shrink}
        vertical={vertical}
        inline={inline}
        width={width}
      >
        {children(bag)}
      </FieldWrapper>
    );
  }, [
    props.label,
    field.value,
    field.onChange,
    field.onBlur,
    meta.error,
    meta.touched,
    hierarchyArea.data,
    hierarchyArea.error,
    hierarchyArea.loading,
    Object.keys(props).length,
    bag.active,
    bag.disabled,
  ]);

  if (hiddenField) {
    return null;
  }

  return fast ? (
    fastField
  ) : (
    <FieldWrapper
      padding={padding}
      basis={basis}
      grow={grow}
      order={order}
      shrink={shrink}
      vertical={vertical}
      inline={inline}
      width={width}
      ref={meta.error ? nodeRef : undefined}
      hasError={!!meta.error}
    >
      {children(bag)}
    </FieldWrapper>
  );
};

FormHierarchyField.propTypes = {
  name: PropTypes.string.isRequired,
  source: PropTypes.func,
  lazy: PropTypes.bool,
  param: PropTypes.string,
  depends: PropTypes.arrayOf(PropTypes.string),
  onDataLoad: PropTypes.func,
  normalizeValue: PropTypes.func,
  validate: PropTypes.func,
  active: PropTypes.bool,
  loading: PropTypes.bool,
  params: PropTypes.func,
};

export default FormHierarchyField;
